import { useParams, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2, Play, Download, X } from "lucide-react";
import { Link } from "react-router-dom";
import { getMeta, getStreams, SeriesMetaSchema } from "@/lib/stremio";
import { proxyUrl } from "@/lib/proxyUrl";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { StreamsList } from "@/components/StreamsList";

export function Series() {
  const navigate = useNavigate();
  const { id, season: selectedSeason, episode: selectedEpisode } = useParams();
  if (!id) throw new Error("No id");

  const {
    data: streamsData,
    isLoading: streamsLoading,
    error: streamsError,
  } = useQuery({
    queryKey: ["streamsData", id, selectedSeason, selectedEpisode],
    queryFn: async () => {
      if (!selectedSeason || !selectedEpisode) return null;
      return await getStreams(
        `series/${id}:${selectedSeason}:${selectedEpisode}`
      );
    },
    enabled: !!selectedSeason && !!selectedEpisode,
  });

  const {
    data: metaData,
    isLoading: metaLoading,
    error: metaError,
  } = useQuery({
    queryKey: ["meta", "series", id],
    queryFn: async () => {
      return await getMeta("series", id);
    },
  });

  if (metaLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (metaError) {
    return (
      <div className="container mx-auto p-8">
        <div className="bg-destructive/10 border-destructive rounded-lg p-6">
          <p className="text-destructive">Error: {metaError.message}</p>
        </div>
      </div>
    );
  }

  if (!metaData) {
    return (
      <div className="container mx-auto p-8">
        <div className="bg-destructive/10 border-destructive rounded-lg p-6">
          <p className="text-destructive">Series not found</p>
        </div>
      </div>
    );
  }

  // Type guard to ensure we're working with series metadata
  const isSeriesMeta = (
    meta: typeof metaData
  ): meta is typeof SeriesMetaSchema.infer => {
    return meta.type === "series";
  };

  // Group episodes by season
  const episodesBySeason =
    isSeriesMeta(metaData) && metaData.videos
      ? metaData.videos.reduce((acc, video) => {
          const season = video.season;
          if (!acc[season]) {
            acc[season] = [];
          }
          acc[season].push(video);
          return acc;
        }, {} as Record<number, typeof metaData.videos>)
      : {};

  // Sort seasons to put season 0 at the end
  const sortedSeasons = Object.entries(episodesBySeason).sort(
    ([seasonA], [seasonB]) => {
      if (seasonA === "0") return 1;
      if (seasonB === "0") return -1;
      return Number(seasonA) - Number(seasonB);
    }
  );

  return (
    <div className="min-h-screen bg-background">
      {metaData.background && (
        <div className="absolute top-0 left-0 right-0 h-[100vh] overflow-hidden">
          <img
            src={proxyUrl(metaData.background)}
            alt="Background"
            className="w-full h-full object-cover bg-gradient-to-t from-background/0 to-primary/50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background to-background/0" />
        </div>
      )}

      <div className="relative mx-auto p-8">
        <Button variant="secondary" asChild>
          <Link to="/series" className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Browse
          </Link>
        </Button>

        <div className="grid md:grid-cols-[300px_1fr] gap-8">
          <img
            src={proxyUrl(metaData.poster)}
            alt={metaData.name}
            className="w-full max-w-[300px] aspect-[2/3] relative rounded-lg overflow-hidden bg-foreground/20"
          />

          <div className="">
            <header className="bg-background/80 p-4 rounded-lg inline-flex flex-col gap-2 mb-4">
              <h1 className="text-4xl font-bold mb-2">{metaData.name}</h1>
              <div className="flex gap-2 text-muted-foreground">
                <span>{metaData.year}</span>
                {metaData.runtime && (
                  <>
                    <span>•</span>
                    <span>{metaData.runtime}</span>
                  </>
                )}
                {isSeriesMeta(metaData) && metaData.seasons && (
                  <>
                    <span>•</span>
                    <span>{metaData.seasons} Seasons</span>
                  </>
                )}
                {isSeriesMeta(metaData) && metaData.episodes && (
                  <>
                    <span>•</span>
                    <span>{metaData.episodes} Episodes</span>
                  </>
                )}
              </div>
            </header>

            {metaData.genres && (
              <div className="flex gap-2 mb-6">
                {metaData.genres.map((genre: string) => (
                  <span
                    key={genre}
                    className="px-3 py-1 rounded-full bg-background/80 text-primary text-sm"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {metaData.description && (
              <p className="text-lg leading-relaxed mb-8">
                {metaData.description}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Episodes Section */}
              <div>
                {isSeriesMeta(metaData) && metaData.videos && (
                  <div className="space-y-4">
                    <h2 className="text-2xl font-semibold mb-4">Episodes</h2>
                    <Accordion
                      type="single"
                      collapsible
                      className="w-full bg-muted/50 p-4 rounded-lg"
                    >
                      {sortedSeasons.map(([season, episodes]) => (
                        <AccordionItem key={season} value={`season-${season}`}>
                          <AccordionTrigger className="hover:no-underline py-2">
                            <span className="text-lg">
                              {season === "0" ? "Special" : `Season ${season}`}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-2 pt-4">
                              {episodes.map((video) => (
                                <Link
                                  key={video.id}
                                  to={`episode/${video.season}/${video.episode}`}
                                  className="block"
                                >
                                  <div
                                    className={cn(
                                      "p-3 border rounded-md hover:bg-accent transition-colors",
                                      selectedSeason === String(video.season) &&
                                        selectedEpisode ===
                                          String(video.episode) &&
                                        "bg-accent"
                                    )}
                                  >
                                    <div className="flex justify-between">
                                      <h3 className="font-medium">
                                        Episode {video.episode}: {video.name}
                                      </h3>
                                      {video.released && (
                                        <span className="text-sm text-muted-foreground">
                                          {new Date(
                                            video.released
                                          ).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                    {video.description && (
                                      <p className="text-sm text-muted-foreground mt-1">
                                        {video.description}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                )}

                {isSeriesMeta(metaData) && !metaData.videos && (
                  <div className="space-y-4">
                    <h2 className="text-2xl font-semibold">Episodes</h2>
                    <p className="text-muted-foreground">
                      No episode information available
                    </p>
                  </div>
                )}
              </div>

              {/* Streams Section */}
              {selectedSeason && selectedEpisode && (
                <div className="sticky top-0 h-min">
                  <StreamsList
                    streams={streamsData?.streams}
                    isLoading={streamsLoading}
                    error={streamsError}
                    title={`Streams for S${selectedSeason}E${selectedEpisode}`}
                    onClose={() => history.back()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Series;
